import {FC, useCallback} from 'react';
import {useParams, useNavigate} from 'react-router';
import {useStateLoader, useBreadcrumbs} from '@hooks/panelHooks';
import {IGiftGroup} from '@interfaces/consumer/daily';
import {Form, Formik, FormikHelpers} from 'formik';
import {toastr} from '@/utils/toastHelper';
import {Card, Col, Row, Spinner} from "react-bootstrap";
import "react-datepicker/dist/react-datepicker.css";
import {FormPageFooter, FormPageHeader} from "@components/falcon/common/forms";
import {BaseInfoBlock} from "./forms";
import * as Yup from 'yup';
import ConsumerDailyService from "@api/consumer/consumerDailyService";
import withPage from "@components/hoc/withPage";
import GiftGroupGiftsBlock from "./forms/giftGroupGiftsBlock";
import {fromUtcDate} from "@services/helpers";

const EditGiftGroupPage: FC = () => {

    const {group_id} = useParams<{ group_id: string }>();
    const loadGiftGroup = useCallback(() => {
        const api = new ConsumerDailyService();
        return api.getGiftGroup(group_id || '');
    }, [group_id]);
    const {data: giftGroup, loading} = useStateLoader<IGiftGroup>(loadGiftGroup);

    useBreadcrumbs('consumer.daily.groups.current.name', true, giftGroup?.name);

    if (!giftGroup) {
        return (
            <Card>
                <Card.Body className="text-center">
                    {loading ? <Spinner className="mr-2" color="primary"/> : (
                        "Группа не найдена"
                    )}
                </Card.Body>
            </Card>
        )
    }

    return (
        <EditGiftGroupForm giftGroup={giftGroup} />
    )
}

interface EditGiftGroupFormProps {
    giftGroup: IGiftGroup;
}

interface EditGiftGroupFormValues {
    name: string;
    startTime: Date;
    endTime: Date;
    isActive: boolean;
}

const EditGiftGroupForm: FC<EditGiftGroupFormProps> = ({giftGroup}) => {

    const navigate = useNavigate();

    const initialValues: EditGiftGroupFormValues = {
        name: giftGroup.name,
        startTime: (giftGroup.startTime && fromUtcDate(giftGroup.startTime).toDate()) || new Date(),
        endTime: (giftGroup.endTime && fromUtcDate(giftGroup.endTime).toDate()) || new Date(),
        isActive: giftGroup.isActive
    }

    const validationSchema = Yup.object<EditGiftGroupFormValues>({
        name: Yup.string()
            .max(256, 'Название не должно превышать 256 символов')
            .required('Введите название'),
        startTime: Yup.date()
            .required('Выберите время начала'),
        endTime: Yup.date()
            .required('Выберите время окончания')
    });

    const onSubmit = async (values: EditGiftGroupFormValues, actions: FormikHelpers<EditGiftGroupFormValues>) => {
        const api = new ConsumerDailyService();
        const result = await api.patchGiftGroup({
            id: giftGroup.id,
            name: values.name,
            startTime: values.startTime.toISOString(),
            endTime: values.endTime.toISOString(),
            isActive: values.isActive
        });

        if (result.success) {
            toastr.success("Изменение группы", "Группа бонусов успешно обновлена");
        }
        else {
            toastr.error('Изменение группы', 'Неизвестная ошибка');
        }
    }

    const onDelete = useCallback(async () => {
        const api = new ConsumerDailyService();
        const result = await api.deleteGiftGroup(giftGroup.id);
        if (result.success) {
            toastr.success("Удаление группы", "Группа бонусов успешно удалена");
            navigate('/consumer/daily/groups/', { replace: true });
        }
        else {
            toastr.error('Удаление группы', 'Неизвестная ошибка');
        }
    }, [giftGroup.id, navigate]);

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={onSubmit}
            validationSchema={validationSchema}
        >
            <Form>
                <Row className="g-3">
                    <Col xs={12}>
                        <FormPageHeader title={`Редактирование группы "${giftGroup.name}"`} />
                    </Col>
                    <Col xs={8}>
                        <BaseInfoBlock />
                    </Col>
                    <Col xs={4}>
                        <GiftGroupGiftsBlock gifts={giftGroup.gifts} />
                    </Col>
                    <Col>
                        <FormPageFooter
                            cancelUrl={'/consumer/daily/groups/'}
                            onDelete={onDelete}
                        />
                    </Col>
                </Row>
            </Form>
        </Formik>
    )
}

export default withPage(EditGiftGroupPage);
